// src/pages/ProductDetailPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import shopApi, { formatPrice } from '../services/shopApi';
import ProductCard from '../components/ProductCard';
import './ProductDetailPage.css';

// Safely convert any spec value to a renderable string
const safeStr = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') {
    if (v.value !== undefined) return `${v.value}${v.unit ? ' ' + v.unit : ''}`;
    return Object.values(v).filter(x => typeof x !== 'object').join(' ');
  }
  return '';
};

// Normalise image to { url, alt } regardless of shape
const normaliseImage = (img, fallbackAlt = '') => {
  if (!img) return null;
  if (typeof img === 'string') return { url: img, alt: fallbackAlt };
  return { url: img.url || img.src || '', alt: img.alt || fallbackAlt };
};

// ── Recently viewed helpers (localStorage) ────────────────────────────────────
const RECENT_KEY = 'jeptags_recently_viewed';
const MAX_RECENT = 8;

function getRecentlyViewed() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

function addToRecentlyViewed(product) {
  try {
    const id = product.productId || product._id;
    const item = {
      productId:    id,
      name:         product.name,
      primaryImage: product.primaryImage || product.images?.[0] || null,
      currentPrice: product.currentPrice || product.pricing?.retail || 0,
      originalPrice:product.originalPrice || product.pricing?.retail || 0,
      currency:     product.currency || product.pricing?.currency || 'USD',
      discountPercent: product.discountPercent || 0,
      brand:        product.brand || product.businessName || '',
      category:     product.category || '',
      inStock:      product.inStock !== false,
      freeShipping: product.freeShipping || false,
      listingId:    product.listingId || null,
    };
    const existing = getRecentlyViewed().filter(r => r.productId !== id);
    const updated  = [item, ...existing].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  } catch {}
}

export default function ProductDetailPage({ onAdd }) {
  const { productId } = useParams();
  const navigate      = useNavigate();

  const [product,       setProduct]       = useState(null);
  const [related,       setRelated]       = useState([]);
  const [recentlyViewed,setRecentlyViewed]= useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [selectedImg,   setSelectedImg]   = useState(0);
  const [quantity,      setQuantity]      = useState(1);
  const [adding,        setAdding]        = useState(false);
  const [added,         setAdded]         = useState(false);
  const [addError,      setAddError]      = useState(null);
  const [activeTab,     setActiveTab]     = useState('description'); // 'description' | 'specs' | 'shipping'

  // Variant selection
  const [selectedVariant, setSelectedVariant] = useState({});
  const [resolvedVariant, setResolvedVariant] = useState(null);

  // ── Load recently viewed on mount ─────────────────────────────────────────────
  useEffect(() => {
    const id = productId;
    const recent = getRecentlyViewed().filter(r => r.productId !== id);
    setRecentlyViewed(recent);
  }, [productId]);

  // ── Load product ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProduct(null);
    setRelated([]);
    setSelectedImg(0);
    setSelectedVariant({});
    setResolvedVariant(null);
    setQuantity(1);
    setActiveTab('description');

    shopApi.getProduct(productId)
      .then(r => {
        if (cancelled) return;
        if (r.success && r.data?.product) {
          const prod = r.data.product;
          setProduct(prod);
          const apiRelated = r.data.relatedProducts || [];
          setRelated(apiRelated);

          // Save to recently viewed
          addToRecentlyViewed(prod);

          // If no related from API, fetch by category
          if (apiRelated.length === 0 && prod.category) {
            shopApi.getProducts({ category: prod.category, limit: 6 })
              .then(cr => {
                if (cancelled) return;
                if (cr.success) {
                  const id = prod.productId || prod._id;
                  setRelated((cr.data?.products || []).filter(p =>
                    (p.productId || p._id) !== id
                  ).slice(0, 4));
                }
              })
              .catch(() => {});
          }
        } else {
          setError('Product not found');
        }
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load product. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [productId]);

  // ── Resolve variant ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!product?.hasVariants || !product?.variants?.length) {
      setResolvedVariant(null);
      return;
    }
    const optionKeys = product.variantOptions
      ? Object.keys(product.variantOptions)
      : Object.keys(product.variants[0]).filter(k =>
          ['color', 'size', 'material'].includes(k) && product.variants[0][k]
        );
    const allChosen = optionKeys.every(k => selectedVariant[k]);
    if (!allChosen) { setResolvedVariant(null); return; }
    const match = product.variants.find(v =>
      optionKeys.every(k => String(v[k]) === String(selectedVariant[k]))
    );
    setResolvedVariant(match || null);
  }, [selectedVariant, product]);

  // ── Effective price ───────────────────────────────────────────────────────────
  const effectivePrice  = resolvedVariant?.price || product?.currentPrice || product?.pricing?.retail || 0;
  const originalPrice   = product?.originalPrice || product?.pricing?.retail || 0;
  const inStock         = product?.hasVariants
    ? resolvedVariant
      ? (resolvedVariant.stock ?? 0) > 0
      : (product.variants || []).some(v => v.isActive !== false && (v.stock ?? 1) > 0)
    : product?.inStock;
  const maxQty = resolvedVariant?.stock || product?.inventory?.currentStock || 99;

  // ── Add to cart ───────────────────────────────────────────────────────────────
  const handleAdd = useCallback(async () => {
    if (!product || adding) return;
    if (product.hasVariants && product.variants?.length > 0 && !resolvedVariant) {
      setAddError('Please select all options before adding to cart.');
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      const variantId = resolvedVariant?.variantId || resolvedVariant?._id?.toString();
      const ok = await onAdd(product.productId || product._id, quantity, variantId);
      if (ok !== false) {
        setAdded(true);
        setTimeout(() => setAdded(false), 2500);
      } else {
        setAddError('Could not add to cart. Please try again.');
      }
    } catch {
      setAddError('Could not add to cart. Please try again.');
    } finally {
      setAdding(false);
    }
  }, [product, adding, resolvedVariant, quantity, onAdd]);

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pd-loading-wrap">
        <div className="spinner" />
        <p>Loading product…</p>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────────
  if (error || !product) {
    return (
      <div className="empty-state" style={{ marginTop: 80 }}>
        <div className="icon">❌</div>
        <h3>{error || 'Product not found'}</h3>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 16 }}>
          <button className="btn-secondary" onClick={() => navigate(-1)}>Go Back</button>
          <Link to="/products" className="btn-primary">Browse Products</Link>
        </div>
      </div>
    );
  }

  // ── Images ────────────────────────────────────────────────────────────────────
  const images = (product.images || [])
    .map(img => normaliseImage(img, product.name))
    .filter(img => img?.url);

  // ── Variant option groups ─────────────────────────────────────────────────────
  const variantOptionGroups = (() => {
    if (!product.hasVariants || !product.variants?.length) return [];
    if (product.variantOptions && typeof product.variantOptions === 'object') {
      return Object.entries(product.variantOptions).map(([key, values]) => ({
        key, label: key.charAt(0).toUpperCase() + key.slice(1),
        values: Array.isArray(values) ? values : [],
      }));
    }
    const keys = ['color', 'size', 'material'].filter(k => product.variants.some(v => v[k]));
    const groups = {};
    keys.forEach(k => {
      groups[k] = [...new Set(product.variants.map(v => v[k]).filter(Boolean))];
    });
    return Object.entries(groups).map(([key, values]) => ({
      key, label: key.charAt(0).toUpperCase() + key.slice(1), values,
    }));
  })();

  const isValueAvailable = (key, value) => {
    if (!product.variants?.length) return true;
    const partial = { ...selectedVariant, [key]: value };
    return product.variants.some(v => {
      const matches = Object.entries(partial).every(([k, val]) => String(v[k]) === String(val));
      return matches && (v.stock === undefined || v.stock > 0) && v.isActive !== false;
    });
  };

  const currency = product.currency || 'USD';
  const discount = product.discountPercent || (originalPrice > effectivePrice
    ? Math.round((1 - effectivePrice / originalPrice) * 100) : 0);

  const hasSpecs    = product.specifications && Object.keys(product.specifications).length > 0;
  const hasShipping = product.shipping?.minDays || product.shipping?.shipFromCountry;

  return (
    <div className="pd-page page-wrap">

      {/* Breadcrumb */}
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span className="bc-sep">/</span>
        <Link to="/products">Products</Link>
        {product.category && (
          <>
            <span className="bc-sep">/</span>
            <Link to={`/products?category=${encodeURIComponent(product.category)}`}>
              {product.category}
            </Link>
          </>
        )}
        <span className="bc-sep">/</span>
        <span>{product.name}</span>
      </nav>

      {/* ── Main product grid ────────────────────────────────────────────────── */}
      <div className="pd-grid">

        {/* Images */}
        <div className="pd-images">
          <div className="pd-main-img">
            {images.length > 0 ? (
              <img
                src={images[selectedImg]?.url}
                alt={images[selectedImg]?.alt || product.name}
                loading="lazy"
              />
            ) : (
              <div className="pd-no-img">📦</div>
            )}
            {discount > 0 && <span className="pd-sale-badge">−{discount}%</span>}
          </div>
          {images.length > 1 && (
            <div className="pd-thumbs">
              {images.map((img, i) => (
                <button
                  key={i}
                  className={`pd-thumb ${selectedImg === i ? 'active' : ''}`}
                  onClick={() => setSelectedImg(i)}
                  aria-label={`Image ${i + 1}`}
                >
                  <img src={img.url} alt={img.alt} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="pd-info">

          {/* Brand */}
          {(product.businessName || product.brand) && (
            <div className="pd-brand">{product.businessName || product.brand}</div>
          )}

          <h1 className="pd-name">{product.name}</h1>

          {/* Rating placeholder */}
          {product.stats?.averageRating > 0 && (
            <div className="pd-rating">
              {'★'.repeat(Math.round(product.stats.averageRating))}
              {'☆'.repeat(5 - Math.round(product.stats.averageRating))}
              {product.stats.totalReviews > 0 && (
                <span className="pd-review-count">({product.stats.totalReviews} reviews)</span>
              )}
            </div>
          )}

          {/* Price */}
          <div className="pd-price-block">
            <span className="pd-price">{formatPrice(effectivePrice, currency)}</span>
            {discount > 0 && originalPrice > effectivePrice && (
              <>
                <span className="pd-orig-price">{formatPrice(originalPrice, currency)}</span>
                <span className="pd-discount-badge">−{discount}% OFF</span>
              </>
            )}
          </div>

          {/* Stock status */}
          <div className={`pd-stock-status ${inStock ? 'in-stock' : 'out-stock'}`}>
            {inStock ? '✓ In Stock' : '✗ Out of Stock'}
          </div>

          {/* Variants */}
          {variantOptionGroups.map(group => (
            <div key={group.key} className="pd-option-group">
              <div className="pd-option-label">
                {group.label}
                {selectedVariant[group.key] && (
                  <span className="pd-selected-val">: {selectedVariant[group.key]}</span>
                )}
              </div>
              <div className="pd-option-values">
                {group.values.map(val => {
                  const avail    = isValueAvailable(group.key, val);
                  const selected = selectedVariant[group.key] === val;
                  return (
                    <button
                      key={val}
                      className={`pd-opt-btn ${selected ? 'selected' : ''} ${!avail ? 'unavailable' : ''}`}
                      onClick={() => avail && setSelectedVariant(prev => ({ ...prev, [group.key]: val }))}
                      disabled={!avail}
                    >{val}</button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Quantity */}
          {inStock && (
            <div className="pd-qty-row">
              <span className="pd-qty-label">Quantity</span>
              <div className="pd-qty">
                <button className="qty-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))} disabled={quantity <= 1}>−</button>
                <span className="qty-val">{quantity}</span>
                <button className="qty-btn" onClick={() => setQuantity(q => Math.min(maxQty, q + 1))} disabled={quantity >= maxQty}>+</button>
              </div>
            </div>
          )}

          {addError && <div className="pd-add-error" role="alert">{addError}</div>}

          {/* Actions */}
          <div className="pd-actions">
            <button
              className={`btn-primary pd-add-btn ${added ? 'added' : ''}`}
              onClick={handleAdd}
              disabled={adding || !inStock || added}
            >
              {added ? '✓ Added to Cart!' : adding ? 'Adding…' : inStock ? 'Add to Cart' : 'Out of Stock'}
            </button>
            <Link to="/cart" className="btn-secondary pd-cart-link">View Cart</Link>
          </div>

          {/* Auth badge */}
          <div className="pd-auth">
            <span className="auth-dot" />
            Verified authentic by JepTags blockchain
          </div>

          {/* Quick shipping info */}
          {hasShipping && (
            <div className="pd-quick-ship">
              {product.freeShipping && <span className="ship-badge-free">✓ Free Shipping</span>}
              {product.shipping?.minDays && product.shipping?.maxDays && (
                <span className="ship-badge-days">📅 {product.shipping.minDays}–{product.shipping.maxDays} days delivery</span>
              )}
              {product.shipping?.shipFromCountry && (
                <span className="ship-badge-from">🌏 Ships from {product.shipping.shipFromCountry}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Tabbed info section ───────────────────────────────────────────────── */}
      <div className="pd-tabs-section">
        <div className="pd-tabs">
          <button
            className={`pd-tab ${activeTab === 'description' ? 'active' : ''}`}
            onClick={() => setActiveTab('description')}
          >Description</button>
          {hasSpecs && (
            <button
              className={`pd-tab ${activeTab === 'specs' ? 'active' : ''}`}
              onClick={() => setActiveTab('specs')}
            >Specifications</button>
          )}
          {hasShipping && (
            <button
              className={`pd-tab ${activeTab === 'shipping' ? 'active' : ''}`}
              onClick={() => setActiveTab('shipping')}
            >Shipping</button>
          )}
        </div>

        <div className="pd-tab-content">
          {activeTab === 'description' && (
            <div className="pd-description-text">
              {product.description
                ? <p>{product.description}</p>
                : <p className="pd-no-desc">No description available.</p>
              }
            </div>
          )}

          {activeTab === 'specs' && hasSpecs && (
            <div className="pd-specs-table">
              {Object.entries(product.specifications)
                .filter(([, v]) => v !== null && v !== undefined && v !== '')
                .map(([k, v]) => {
                  const display = safeStr(v);
                  if (!display) return null;
                  return (
                    <div key={k} className="pd-spec-row">
                      <span className="pd-spec-key">
                        {k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                      </span>
                      <span className="pd-spec-val">{display}</span>
                    </div>
                  );
                })}
            </div>
          )}

          {activeTab === 'shipping' && hasShipping && (
            <div className="pd-shipping-info">
              {product.shipping?.shipFromCountry && (
                <div className="ship-info-row">
                  <span className="ship-info-icon">🌏</span>
                  <div>
                    <strong>Ships from</strong>
                    <p>{product.shipping.shipFromCountry}</p>
                  </div>
                </div>
              )}
              {product.shipping?.minDays && product.shipping?.maxDays && (
                <div className="ship-info-row">
                  <span className="ship-info-icon">📅</span>
                  <div>
                    <strong>Delivery time</strong>
                    <p>{product.shipping.minDays}–{product.shipping.maxDays} business days</p>
                  </div>
                </div>
              )}
              {product.freeShipping && (
                <div className="ship-info-row">
                  <span className="ship-info-icon">✓</span>
                  <div>
                    <strong>Free Shipping</strong>
                    <p>No shipping cost for this product</p>
                  </div>
                </div>
              )}
              {product.shipping?.label && (
                <div className="ship-info-row">
                  <span className="ship-info-icon">🚚</span>
                  <div>
                    <strong>Shipping method</strong>
                    <p>{product.shipping.label}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Related products ──────────────────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="pd-section">
          <div className="pd-section-header">
            <h2 className="pd-section-title">Related Products</h2>
            {product.category && (
              <Link
                to={`/products?category=${encodeURIComponent(product.category)}`}
                className="pd-section-link"
              >
                See all →
              </Link>
            )}
          </div>
          <div className="related-grid">
            {related.map(p => (
              <ProductCard
                key={p.listingId || p._id || p.productId}
                product={p}
                onAdd={(prod) => onAdd(prod.productId || prod._id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Recently viewed ───────────────────────────────────────────────────── */}
      {recentlyViewed.length > 0 && (
        <section className="pd-section">
          <div className="pd-section-header">
            <h2 className="pd-section-title">Recently Viewed</h2>
            <Link to="/products" className="pd-section-link">Browse all →</Link>
          </div>
          <div className="related-grid">
            {recentlyViewed.slice(0, 4).map(p => (
              <ProductCard
                key={p.productId}
                product={p}
                onAdd={(prod) => onAdd(prod.productId || prod._id)}
              />
            ))}
          </div>
        </section>
      )}

    </div>
  );
}